import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { SupportChatWidget } from "@/components/support";
import { ScrollToTop } from "@/components/shared/ScrollToTop";
import { ProtectedRoute } from "@/components/shared/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";

// Lazy load all pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Confirmation = lazy(() => import("./pages/Confirmation"));
const Login = lazy(() => import("./pages/Login"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const EspacePro = lazy(() => import("./pages/EspacePro"));
const Cart = lazy(() => import("./pages/Cart"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const Admin = lazy(() => import("./pages/Admin"));
const Profile = lazy(() => import("./pages/Profile"));
const ScanTicket = lazy(() => import("./pages/ScanTicket"));
const SeatingAdmin = lazy(() => import("./pages/SeatingAdmin"));
const EventSeating = lazy(() => import("./pages/EventSeating"));
const SeatCheckout = lazy(() => import("./pages/SeatCheckout"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Support = lazy(() => import("./pages/Support"));
const PitchDeck = lazy(() => import("./pages/PitchDeck"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes garbage collection
    },
  },
});

// Branded loading screen matching the HTML initial loader
const PageLoadingSpinner = () => (
  <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-5">
    <div
      className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-coral flex items-center justify-center"
      style={{ animation: "pulse-glow 2s ease-in-out infinite" }}
    >
      <svg viewBox="0 0 24 24" className="w-7 h-7 stroke-white fill-none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 9a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V9Z"/>
        <path d="M9 6v12"/>
        <circle cx="15" cy="12" r="1.5"/>
      </svg>
    </div>
    <span className="font-display text-2xl font-bold text-foreground tracking-tight">E-tickets</span>
    <span className="text-sm text-muted-foreground">Billetterie en ligne</span>
    <div className="w-[120px] h-[3px] rounded-full bg-foreground/[0.08] overflow-hidden">
      <div
        className="w-[40%] h-full rounded-full bg-gradient-to-r from-primary to-coral"
        style={{ animation: "progress-slide 1.4s ease-in-out infinite" }}
      />
    </div>
  </div>
);
// Only show chat widget on public-facing pages
const CHAT_EXCLUDED_PATHS = ['/admin', '/event-lab', '/pitch', '/scan', '/seating-admin'];

function ConditionalChatWidget() {
  const location = useLocation();
  const shouldHide = CHAT_EXCLUDED_PATHS.some(path => location.pathname.startsWith(path));
  if (shouldHide) return null;
  return <SupportChatWidget />;
}

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CartProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <Suspense fallback={<PageLoadingSpinner />}>
                <main id="main-content">
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/event/:id" element={<EventDetail />} />
                  <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                  <Route path="/checkout/:id" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                  <Route path="/confirmation" element={<ProtectedRoute><Confirmation /></ProtectedRoute>} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/admin-login" element={<AdminLogin />} />
                  <Route path="/event-lab" element={<EspacePro />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="/scan" element={<ProtectedRoute><ScanTicket /></ProtectedRoute>} />
                  <Route path="/seating-admin" element={<ProtectedRoute><SeatingAdmin /></ProtectedRoute>} />
                  <Route path="/event/:id/seating" element={<EventSeating />} />
                  <Route path="/event/:id/seating/:layoutId" element={<EventSeating />} />
                  <Route path="/seat-checkout/:id" element={<ProtectedRoute><SeatCheckout /></ProtectedRoute>} />
                  <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
                  <Route path="/pitch" element={<PitchDeck />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </main>
              </Suspense>
              <ConditionalChatWidget />
            </BrowserRouter>
          </TooltipProvider>
        </CartProvider>
      </AuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  </ThemeProvider>
);

export default App;
